// Lista de ítems de contenido (texto/audio/vídeo) de una práctica de conversación.

import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit, FileText, Headphones, Video } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { Badge } from '@/components/atoms/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { useConversationContent } from '@/hooks/useConversationData';
import { conversationBackofficeRoutes } from '@/constants/conversationBackofficeRoutes';
import { formatDuration } from '@/utils/formatTime';
import styles from './ConversationContentPage.module.scss';

const CONTENT_TYPE_ICONS: Record<string, React.ReactNode> = {
  text: <FileText size={16} />,
  audio: <Headphones size={16} />,
  video: <Video size={16} />,
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
  text: 'Texto',
  audio: 'Audio',
  video: 'Vídeo',
};

export function ConversationContentPage() {
  const navigate = useNavigate();
  const { resourceId } = useParams<{ resourceId: string }>();
  const { content, isLoading, deleteContent } = useConversationContent(resourceId);

  const handleAddContent = () => {
    if (resourceId) {
      navigate(conversationBackofficeRoutes.contentNew(resourceId));
    }
  };

  const handleEditContent = (contentId: string) => {
    if (resourceId) {
      navigate(conversationBackofficeRoutes.contentEdit(resourceId, contentId));
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    if (window.confirm('¿Seguro que quieres eliminar este contenido?')) {
      try {
        await deleteContent(contentId);
      } catch (error) {
        console.error('Error deleting content:', error);
        alert('Error al eliminar el contenido');
      }
    }
  };

  const handleBack = () => {
    if (resourceId) {
      navigate(`${conversationBackofficeRoutes.edit(resourceId)}?tab=resources`);
    } else {
      navigate(-1);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.resourceContent}>
        <div className={styles.resourceContent__loading}>Cargando contenido...</div>
      </div>
    );
  }

  return (
    <div className={styles.resourceContent}>
      <div className={styles.resourceContent__header}>
        <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver a la conversación" />
        <Button leftIcon={<Plus />} onClick={handleAddContent}>
          Añadir contenido
        </Button>
      </div>

      <div>
        <h1 className={styles.resourceContent__title}>Editor de contenido</h1>
        <p className={styles.resourceContent__subtitle}>
          ID conversación: {resourceId} · {content.length}{' '}
          {content.length === 1 ? 'elemento' : 'elementos'}
        </p>
      </div>

      <div className={styles.resourceContent__content}>
        {content.length === 0 ? (
          <Card>
            <CardContent className={styles.resourceContent__empty}>
              <p>Sin contenido. Haz clic en "Añadir contenido" para crear uno.</p>
            </CardContent>
          </Card>
        ) : (
          <div className={styles.resourceContent__list}>
            {content.map((contentItem) => (
              <Card key={contentItem.id} className={styles.resourceContent__item}>
                <CardHeader className={styles.resourceContent__itemHeader}>
                  <div className={styles.resourceContent__itemInfo}>
                    <div className={styles.resourceContent__itemBadges}>
                      <Badge variant="default">
                        <span className={styles.resourceContent__badgeIcon}>
                          {CONTENT_TYPE_ICONS[contentItem.type]}
                        </span>
                        {CONTENT_TYPE_LABELS[contentItem.type]}
                      </Badge>
                      {contentItem.duration ? (
                        <Badge variant="info">{formatDuration(contentItem.duration)}</Badge>
                      ) : null}
                      {contentItem.transcript && (
                        <Badge variant="success">Transcript</Badge>
                      )}
                      <span className={styles.resourceContent__itemOrder}>
                        Orden: {contentItem.order}
                      </span>
                    </div>
                    <CardTitle>{contentItem.title}</CardTitle>
                    {contentItem.type === 'text' && contentItem.content && (
                      <p className={styles.resourceContent__itemPreview}>
                        {contentItem.content.slice(0, 120)}
                        {contentItem.content.length > 120 ? '…' : ''}
                      </p>
                    )}
                    {(contentItem.type === 'audio' || contentItem.type === 'video') &&
                      contentItem.url && (
                        <p className={styles.resourceContent__itemPreview}>{contentItem.url}</p>
                      )}
                  </div>
                  <div className={styles.resourceContent__itemActions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Edit />}
                      onClick={() => handleEditContent(contentItem.id)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      leftIcon={<Trash2 />}
                      onClick={() => handleDeleteContent(contentItem.id)}
                    >
                      Eliminar
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

export default ConversationContentPage;
